import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";

import { AnimatedPage, AnimatedCard, StaggerContainer, StaggerItem, ParticleField } from "../BizDevAnimations";
import "../BizDevAnimations.css";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";
import usePullToRefresh from "../hooks/usePullToRefresh";
import MobileSkeleton from "../components/MobileSkeleton";
import useShortcuts from "../hooks/useShortcuts";
import useUrlState from "../hooks/useUrlState";
import useIsMobile from "../hooks/useIsMobile";

const KANBAN_COLUMNS = [
  { id: "new", label: "New", stages: ["Cold", "Warm", "New"], color: "#64748B" },
  { id: "engaged", label: "Engaged", stages: ["Hot", "Qualified", "Follow-up", "Engaged"], color: "#F59E0B" },
  { id: "pitched", label: "Pitched", stages: ["RFP Received", "Proposal Sent", "Pitched"], color: "#8B5CF6" },
  { id: "negotiation", label: "Negotiation", stages: ["Negotiation"], color: "#EC4899" },
  { id: "won", label: "Won", stages: ["Won"], color: "#22C55E" },
];
const FUNNEL_STAGES = ["New", "Engaged", "Pitched", "Negotiation", "Won", "Lost", "On Hold"];
const STAGE_COLORS = { Cold: "#64748B", Warm: "#F59E0B", Hot: "#EF4444", Qualified: "#8B5CF6", "RFP Received": "#06B6D4", "Proposal Sent": "#3B82F6", "Follow-up": "#F97316", Pitched: "#A855F7", Negotiation: "#EC4899", Won: "#22C55E", Lost: "#6B7280", "On Hold": "#94A3B8" };
const STAGE_BG = Object.fromEntries(Object.entries(STAGE_COLORS).map(([k, v]) => [k, v + "18"]));
const fmt = (v) => { if (!v && v !== 0) return ""; if (Math.abs(v) >= 100) return `₹${(v/100).toFixed(1)}Cr`; return `₹${v.toFixed(0)}L`; };

function CompanyLogo({ name, website, size = 24 }) {
  const [failed, setFailed] = useState(false);
  const domain = website && website !== "Unknown" ? website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : null;
  if (domain && !failed) {
    return <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} width={size} height={size} alt={name} style={{ borderRadius: size > 30 ? 10 : 6, objectFit: "contain", background: "var(--s3)", flexShrink: 0 }} onError={() => setFailed(true)} />;
  }
  const initials = (name || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: size > 30 ? 10 : 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 700, background: "var(--s3)", color: "var(--t2)", flexShrink: 0 }}>{initials}</div>;
}

function LeadCard({ lead, onDragStart, onClick }) {
  const canViewFinancials = useCanViewFinancials();
  const col = STAGE_COLORS[lead.funnel_stage] || "#666";
  return (
    <div draggable onDragStart={(e) => { e.dataTransfer.setData("lead_id", lead.lead_id); e.dataTransfer.setData("from_stage", lead.funnel_stage); if (onDragStart) onDragStart(lead); }}
      onClick={() => onClick(lead)}
      className="dm-lead-card"
      style={{ background: 'var(--s2)', borderLeft: `2px solid ${col}`, borderRadius: 8, padding: '10px 12px', cursor: 'grab', marginBottom: 6, transition: 'transform .15s, box-shadow .15s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CompanyLogo name={lead.company_name} website={lead.website} size={20} />
          <div className="dm-lead-name" style={{ fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>{lead.company_name}</div>
        </div>
        {lead.ai_score > 0 && <span style={{ fontFamily: 'var(--font-orb)', fontSize: 9, color: lead.ai_score > 80 ? 'var(--r)' : 'var(--a)', background: lead.ai_score > 80 ? 'var(--rd)' : 'var(--ad)', padding: '1px 5px', borderRadius: 3 }}>★{lead.ai_score}</span>}
      </div>
      {lead.industry && lead.industry !== "Unknown" && <div className="dm-lead-secondary" style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', marginBottom: 4 }}>{lead.industry}</div>}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {lead.cy_revenue_lakhs > 0 && <span className="dm-lead-secondary" style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--g)' }}>{canViewFinancials ? fmt(lead.cy_revenue_lakhs) : "---"}</span>}
        <span className="dm-lead-secondary" style={{ fontFamily: 'var(--font-mono)', fontSize: 7, padding: '1px 5px', borderRadius: 10, background: STAGE_BG[lead.funnel_stage] || '#333', color: STAGE_COLORS[lead.funnel_stage] || '#888' }}>{lead.funnel_stage}</span>
        {lead.is_retainer && <span className="dm-lead-secondary" style={{ fontFamily: 'var(--font-mono)', fontSize: 7, padding: '1px 5px', borderRadius: 10, background: 'var(--cd)', color: 'var(--c)' }}>RETAINER</span>}
      </div>
    </div>
  );
}

function KanbanColumn({ column, leads, onDrop, onLeadClick }) {
  const total = leads.reduce((s, l) => s + (l.cy_revenue_lakhs || 0), 0);
  return (
    <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const lid = e.dataTransfer.getData("lead_id"); const from = e.dataTransfer.getData("from_stage"); if (lid) onDrop(lid, from, column.stages[0]); }}
      style={{ flex: 1, minWidth: 200, background: 'var(--s1)', border: '1px solid var(--bdr)', borderTop: `2px solid ${column.color}`, borderRadius: 10, padding: 10, minHeight: 300 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.08em' }}>{column.label}</div>
        <span style={{ fontFamily: 'var(--font-orb)', fontSize: 10, color: column.color, background: column.color + '18', padding: '1px 6px', borderRadius: 4 }}>{leads.length}</span>
      </div>
      {total > 0 && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', marginBottom: 8 }}>{fmt(total)}</div>}
      {leads.map(l => <LeadCard key={l.lead_id} lead={l} onClick={onLeadClick} />)}
      {leads.length === 0 && <div style={{ padding: 20, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)' }}>Drop leads here</div>}
    </div>
  );
}

export default function BizDevLeads() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canViewFinancials = useCanViewFinancials();
  const isMobile = useIsMobile();
  const searchRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // URL-backed so reload + deep-links + mobile default all work
  const [view, setView] = useUrlState("view", isMobile ? "list" : "kanban");
  const [search, setSearch] = useUrlState("q", "");
  const [stageFilter, setStageFilter] = useUrlState("stage", "All");
  const [showAddModal, setShowAddModal] = useState(searchParams.get("new") === "1");
  const [addForm, setAddForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", industry: "", estimated_value: "", funnel_stage: "Cold", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadLeads = useCallback(async () => {
    try { setLeads((await api.get("/leads")).data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  usePullToRefresh(loadLeads);

  // Global keyboard shortcuts — desktop only (useShortcuts auto-disables on touch)
  useShortcuts(
    [
      { keys: "/", description: "Focus search", group: "Leads",
        handler: () => searchRef.current?.focus() },
      { keys: "n", description: "New lead", group: "Leads",
        handler: () => setShowAddModal(true) },
      { keys: "escape", description: "Close add modal",
        handler: () => setShowAddModal((v) => v ? false : v) },
    ],
    { enabled: !loading }
  );

  // Optimistic stage change — apply locally first, rollback if server rejects
  const handleStageChange = async (leadId, fromStage, newStage) => {
    if (fromStage === newStage) return;
    const snap = leads;
    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, funnel_stage: newStage } : l));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    try {
      await api.put(`/leads/${leadId}/stage`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch (e) {
      setLeads(snap);
      toast.error("Failed to move lead");
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!addForm.company_name.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { company_name: addForm.company_name, lead_source: "Direct Lead", funnel_stage: addForm.funnel_stage || "Cold", notes: addForm.notes, website: "", is_retainer: false });
      toast.success("Lead created!");
      setShowAddModal(false);
      setAddForm({ company_name: "", contact_name: "", email: "", phone: "", industry: "", estimated_value: "", funnel_stage: "Cold", notes: "" });
      loadLeads();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const filtered = leads.filter(l => {
    if (search && !l.company_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (stageFilter !== "All" && l.funnel_stage !== stageFilter) return false;
    return true;
  });

  if (loading) return <MobileSkeleton type="list" />;

  return (
    <AnimatedPage>
    <div className="dm-page-container" style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div className="dm-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-orb)', fontSize: 18, fontWeight: 700, color: 'var(--t1)', letterSpacing: '.06em' }}>LEADS</h1>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', marginTop: 2 }}>{leads.length} leads tracked</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search… (/)" style={{ background: 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 6, padding: '6px 10px', fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--t1)', outline: 'none', width: 160 }} />
          <button onClick={() => setView("kanban")} className="dm-view-toggle" style={{ padding: '5px 10px', background: view === "kanban" ? 'var(--rd)' : 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 8, color: view === "kanban" ? 'var(--r)' : 'var(--t3)', cursor: 'pointer' }}>KANBAN</button>
          <button onClick={() => setView("list")} className="dm-view-toggle" style={{ padding: '5px 10px', background: view === "list" ? 'var(--rd)' : 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 8, color: view === "list" ? 'var(--r)' : 'var(--t3)', cursor: 'pointer' }}>LIST</button>
          <button onClick={() => setShowAddModal(true)} className="dm-desktop-only" style={{ padding: '5px 14px', background: 'var(--r)', border: 'none', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 8, color: '#fff', cursor: 'pointer', letterSpacing: '.06em' }}>+ ADD LEAD</button>
        </div>
      </div>

      {/* Kanban View */}
      {view === "kanban" && (
        <div className="dm-kanban-row" style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10 }}>
          {KANBAN_COLUMNS.map(col => (
            <KanbanColumn key={col.id} column={col} leads={filtered.filter(l => col.stages.includes(l.funnel_stage))}
              onDrop={(lid, from, to) => handleStageChange(lid, from, to)} onLeadClick={l => navigate(`/bizdev/leads/${l.lead_id}`)} />
          ))}
        </div>
      )}

      {/* List View */}
      {view === "list" && (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
            {["All", ...FUNNEL_STAGES].map(s => (
              <button key={s} onClick={() => setStageFilter(s)} style={{
                fontFamily: 'var(--font-mono)', fontSize: 7, padding: '4px 10px', borderRadius: 20,
                border: '1px solid', cursor: 'pointer',
                background: stageFilter === s ? 'var(--rd)' : 'transparent',
                borderColor: stageFilter === s ? 'rgba(227,25,55,.3)' : 'var(--bdr)',
                color: stageFilter === s ? 'var(--r)' : 'var(--t3)',
              }}>{s}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
            {filtered.map(l => <LeadCard key={l.lead_id} lead={l} onClick={lead => navigate(`/bizdev/leads/${lead.lead_id}`)} />)}
          </div>
        </>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={() => setShowAddModal(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.7)' }} />
          <form onSubmit={handleAddLead} style={{ position: 'relative', background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}><div style={{ fontFamily: 'var(--font-orb)', fontSize: 14, color: 'var(--t1)' }}>ADD LEAD</div><button type="button" onClick={() => setShowAddModal(false)} style={{ background: 'var(--s3)', border: '1px solid var(--bdr)', borderRadius: 6, padding: '4px 10px', color: 'var(--t2)', cursor: 'pointer', fontSize: 16, lineHeight: 1, transition: 'all .15s' }} onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,.15)'; e.currentTarget.style.color = '#EF4444'; }} onMouseLeave={e => { e.currentTarget.style.background = 'var(--s3)'; e.currentTarget.style.color = 'var(--t2)'; }} title="Close">&#10005;</button></div>
            <input value={addForm.company_name} onChange={e => setAddForm({ ...addForm, company_name: e.target.value })} placeholder="Company Name *" required className="form-input" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input value={addForm.contact_name} onChange={e => setAddForm({ ...addForm, contact_name: e.target.value })} placeholder="Contact Name" className="form-input" />
              <input value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} placeholder="Email" className="form-input" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} placeholder="Phone" className="form-input" />
              <input value={addForm.industry} onChange={e => setAddForm({ ...addForm, industry: e.target.value })} placeholder="Industry" className="form-input" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input value={addForm.estimated_value} onChange={e => setAddForm({ ...addForm, estimated_value: e.target.value })} placeholder="Est. Value (₹L)" type="number" className="form-input" />
              <select value={addForm.funnel_stage} onChange={e => setAddForm({ ...addForm, funnel_stage: e.target.value })} className="form-input" style={{ background: 'var(--bg)' }}>
                {FUNNEL_STAGES.map(s => <option key={s} value={s} style={{ background: '#111' }}>{s}</option>)}
              </select>
            </div>
            <textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Notes" rows={3} className="form-input" style={{ resize: 'none' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '10px', background: 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 8, color: 'var(--t2)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12 }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{ flex: 1, padding: '10px', background: 'var(--r)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600 }}>{submitting ? "Creating..." : "Create Lead"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
    </AnimatedPage>
  );
}
